import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import React, { useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/ui/date-input";
import SelectUser from "@/components/features/users/select-user";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import SelectPlayer from "../../players/select-player";
import SelectReportTemplate from "../../reports/templates/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTRPC } from "@/trpc";
import * as Icons from "lucide-react";
import SelectClub from "../../clubs/select-club";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import CreateContact from "../../contacts/create";
import SelectContact from "../../contacts/select";
import SelectCompetition from "../../competitions/select-competition";

interface Props {
  task_id: string;
  defaultValues?: Partial<UpdateTaskSchema>;
  trigger?: React.ReactNode;
  onUpdated?: () => void;
}

const updateTaskSchema = z.object({
  task_id: z.string().uuid(),
  assigned_user_id: z.string().min(1, "Gebruiker is verplicht").optional(),
  report_id: z.string().min(1, "Rapport is verplicht").optional(),
  expiration_date: z.string().optional(),
  mongo_player_id: z.string().optional(),
  club_id: z.string().optional(),
  priority: z.enum(["low", "medium", "high"]).optional(),
  note: z.string().optional(),
  source: z.string().optional(),
  offered_by_id: z.string().uuid().optional(),
  ext_competition_id: z.string().optional(),
  ext_club_id: z.string().optional(),
});

type UpdateTaskSchema = z.infer<typeof updateTaskSchema>;

const UpdateTask = ({ task_id, trigger, defaultValues, onUpdated }: Props) => {
  const { trpc } = useTRPC();
  const qc = useQueryClient(); 
  const [open, setOpen] = React.useState(false);
  const rapportDisabled = defaultValues?.report_id ? true : undefined;

  const form = useForm<UpdateTaskSchema>({
    resolver: zodResolver(updateTaskSchema),
    defaultValues: { task_id, ...defaultValues },
    mode: "onChange",
    reValidateMode: "onChange"
  });

  useEffect(() => {
    if (defaultValues && open) {
      form.reset({ task_id, ...defaultValues });
    }
  }, [open, defaultValues, task_id, form]);

  const playerId = form.watch("mongo_player_id");
  const clubId = form.watch("ext_club_id");
  const competitionId = form.watch("ext_competition_id");

  const { data: playerInfo } = useQuery({
    queryKey: ["playerInfo", playerId],
    enabled: !!playerId,
    queryFn: () => (playerId ? trpc.players.getById.query({ player_id: playerId }) : null),
  });

  const { data: compsForClub } = useQuery({
    queryKey: ["compsForClub", clubId],
    enabled: !!clubId,
    queryFn: async () => {
      if (!clubId) return [];
      const res = await trpc.competitions.search.query({
        club: { club_id: clubId },
        onlyCurrentSeason: true,
        all: true,
      });
      return res.competitions ?? [];
    },
  });

  const { data: clubsForComp } = useQuery({
    queryKey: ["clubsForComp", competitionId],
    enabled: !!competitionId,
    queryFn: async () => {
      if (!competitionId) return [];
      const res = await trpc.clubs.search.query({
        competition: { competition_id: competitionId },
        all: true,
      });
      return res.clubs ?? [];
    },
  });

  useEffect(() => {
    if (!playerInfo) return;
    if (playerInfo.clubId && playerInfo.clubId !== clubId) {
      form.setValue("ext_club_id", playerInfo.clubId, { shouldDirty: true, shouldValidate: true });
    }
  }, [playerInfo, clubId, form]);

  useEffect(() => {
    if (!compsForClub || !competitionId) return;
    const ok = compsForClub.some((c: any) => c.id === competitionId);
    if (!ok) {
      form.setValue("ext_competition_id", undefined, { shouldDirty: true, shouldValidate: true });
    }
  }, [compsForClub, competitionId, form]);

  useEffect(() => {
    if (!clubId || !compsForClub) return;

    if (competitionId) {
      const ok = compsForClub.some((c: any) => c.id === competitionId);
      if (!ok) {
        form.setValue("ext_competition_id", undefined, { shouldDirty: true, shouldValidate: true });
      }
    } else if (compsForClub.length === 1) {
      form.setValue("ext_competition_id", compsForClub[0].id, { shouldDirty: true, shouldValidate: true });
    }

    if (playerInfo?.clubId && playerInfo.clubId !== clubId) {
      form.setValue("mongo_player_id", undefined, { shouldDirty: true, shouldValidate: true });
    }
  }, [clubId, compsForClub, competitionId, playerInfo, form]);

  useEffect(() => {
    if (!competitionId || !clubsForComp) return;

    if (clubId) {
      const ok = clubsForComp.some((cl: any) => cl.id === clubId);
      if (!ok) {
        form.setValue("ext_club_id", undefined, { shouldDirty: true, shouldValidate: true });
        form.setValue("mongo_player_id", undefined, { shouldDirty: true, shouldValidate: true });
      }
    } else {
      if (clubsForComp.length === 1) {
        form.setValue("ext_club_id", clubsForComp[0].id, { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [competitionId, clubsForComp, clubId, form]);

  const compSearchOptions = useMemo(
    () => ({
      club: clubId
        ? { club_id: clubId }
        : playerInfo?.clubId
        ? { club_id: playerInfo.clubId }
        : undefined,
    }),
    [clubId, playerInfo?.clubId]
  );

  const clubSearchOptions = useMemo(
    () => ({
      competition: {
        competition_id: competitionId,
        club_id: playerInfo?.clubId ? playerInfo.clubId : undefined,
      },
    }),
    [competitionId, playerInfo?.clubId]
  );

  const playerSearchOptions = useMemo(
    () => ({
      competition: {
        club_id: clubId,
        competition_id: competitionId,
      },
    }),
    [clubId, competitionId]
  );

  const compOptionsKey = useMemo(() => JSON.stringify(compSearchOptions ?? {}), [compSearchOptions]);
  const clubOptionsKey = useMemo(() => JSON.stringify(clubSearchOptions ?? {}), [clubSearchOptions]);
  const playerOptionsKey = useMemo(() => JSON.stringify(playerSearchOptions ?? {}), [playerSearchOptions]);

  useEffect(() => {
    if (!clubId && !playerInfo?.clubId) return;
    qc.prefetchQuery({
      queryKey: ["competitions", "", compOptionsKey, "all"],
      queryFn: () =>
        trpc.competitions.search.query({
          onlyCurrentSeason: true,
          all: true,
          ...(compSearchOptions.club ? { club: compSearchOptions.club } : {}),
        }),
      staleTime: 30_000,
    });
  }, [clubId, playerInfo?.clubId, compOptionsKey, trpc, qc]);

  useEffect(() => {
    if (!competitionId) return;
    qc.prefetchQuery({
      queryKey: ["clubs", "", clubOptionsKey, "all"],
      queryFn: () =>
        trpc.clubs.search.query({
          all: true,
          ...clubSearchOptions,
        }),
      staleTime: 30_000,
    });
  }, [competitionId, clubOptionsKey, trpc, qc]);

  useEffect(() => {
    if (!clubId && !competitionId) return;
    qc.prefetchQuery({
      queryKey: ["players", "", playerOptionsKey, "all"],
      queryFn: () =>
        trpc.players.search.query({
          all: true,
          ...playerSearchOptions,
        }),
      staleTime: 30_000,
    });
  }, [clubId, competitionId, playerOptionsKey, trpc, qc]);


  const { mutate: updateTask, isLoading } = useMutation({
    mutationFn: async (data: UpdateTaskSchema) => {
      await trpc.tasks.update.mutate(data);
    },
    onSuccess: () => {
      toast.success("Taak succesvol gewijzigd");
      onUpdated?.();
      setOpen(false);
    },
  });

  const handleSubmit = () => {
    if (!form.formState.isValid) return;
    updateTask(form.getValues());
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="md:max-w-3xl">
        <SheetHeader>
          <SheetTitle>Taak wijzigen</SheetTitle>
          <SheetDescription>
            Vul de velden in om een taak te wijzigen
          </SheetDescription>
        </SheetHeader>
        <div className="h-full overflow-auto px-4">
          <div className="flex flex-col gap-4">
            <div>
              <Label required>Toegewezen scout</Label>
              <Controller
                control={form.control}
                name="assigned_user_id"
                render={({ field }) => (
                  <SelectUser value={field.value} onValueChange={(v) => { field.onChange(v); }} />
                )}
              />
            </div>

            <div>
              <Label>Prioriteit</Label>
              <Controller
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <Select value={field.value ?? undefined} onValueChange={(v) => { field.onChange(v); }}>
                    <SelectTrigger><SelectValue placeholder="Selecteer prioriteit" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Laag</SelectItem>
                      <SelectItem value="medium">Gemiddeld</SelectItem>
                      <SelectItem value="high">Hoog</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="grid md:grid-cols-4 gap-2">
              <div>
                <Label>Deadline</Label>
                <Controller
                  control={form.control}
                  name="expiration_date"
                  render={({ field }) => (
                    <DateInput
                      date={field.value ? new Date(field.value) : undefined}
                      onDateChange={(d) => field.onChange(d ? d.toISOString() : undefined)}
                    />
                  )}
                />
              </div>
            </div>

            <div>
              <Label>Competitie</Label>
              <Controller
                control={form.control}
                name="ext_competition_id"
                render={({ field }) => (
                  <SelectCompetition
                    value={field.value}
                    onValueChange={field.onChange}
                    searchOptions={compSearchOptions}
                  />
                )}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-2">
              <div>
                <Label>Club</Label>
                <Controller
                  control={form.control}
                  name="ext_club_id"
                  render={({ field }) => (
                    <SelectClub
                      value={field.value ?? undefined}
                      onValueChange={field.onChange}
                      searchOptions={clubSearchOptions}
                    />
                  )}
                />
              </div>

              <div>
                <Label>Speler</Label>
                <Controller
                  control={form.control}
                  name="mongo_player_id"
                  render={({ field }) => (
                    <SelectPlayer
                      value={field.value}
                      onValueChange={field.onChange}
                      searchOptions={playerSearchOptions}
                    />
                  )}
                />
              </div>
            </div>

            <div>
              <Label>Rapport</Label>
              <Controller
                control={form.control}
                name="report_id"
                render={({ field }) => (
                  <SelectReportTemplate
                    disabled={rapportDisabled}
                    value={field.value}
                    onValueChange={(v) => { field.onChange(v); }}
                  />
                )}
              />
            </div>

            <div>
              <Label>Bron</Label>
              <Controller
                control={form.control}
                name="source"
                render={({ field }) => (
                  <Select value={field.value ?? undefined} onValueChange={(v) => { field.onChange(v); }}>
                    <SelectTrigger><SelectValue placeholder="Selecteer bron" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="network">Eigen netwerk</SelectItem>
                      <SelectItem value="data">Data</SelectItem>
                      <SelectItem value="impect">Impect</SelectItem>
                      <SelectItem value="other">Andere</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label>Aangeboden door</Label>
                <CreateContact
                  onCreated={(newContact) => {
                    form.setValue("offered_by_id", newContact.id);
                  }}
                  trigger={
                    <Button size="xs" variant="outline">
                      <Icons.Plus className="w-4 h-4" />
                      Nieuw contact
                    </Button>
                  }
                />
              </div>
              <Controller
                control={form.control}
                name="offered_by_id"
                render={({ field }) => (
                  <SelectContact
                    value={field.value ?? undefined}
                    onValueChange={(v) => { field.onChange(v); }}
                  />
                )}
              />
            </div>

            <div>
              <Label>Notitie aan scout</Label>
              <Controller
                control={form.control}
                name="note"
                render={({ field }) => (
                  <Textarea value={field.value ?? undefined} onChange={(e) => { field.onChange(e.currentTarget.value); }} />
                )}
              />
            </div>
          </div>
        </div>
        <SheetFooter>
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={() => {
                setOpen(false);
              }}
              variant="outline"
            >
              Sluiten
            </Button>
            <Button
              disabled={!form.formState.isValid || isLoading}
              onClick={() => {
                handleSubmit();
              }}
            >
              {isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
              Opslaan
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default UpdateTask;
